// SCENTREAT v3 home (07/10/2026): rails (dots + arrows under the rail, mouse drag) and the pure-oils pack builder.
// Loaded as a module on the home page only (layout/theme.liquid). Idempotent per element (data-s3-ready) and
// re-run on shopify:section:load so the theme editor keeps working.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

const ready = (el, key) => { if (el.dataset[key]) return false; el.dataset[key] = '1'; return true; };

function initRail(rail) {
  if (!ready(rail, 's3Ready')) return;
  const ctrl = document.querySelector(`[data-s3-ctrl="${rail.id}"]`);
  const gap = () => parseFloat(getComputedStyle(rail).columnGap) || 0;
  const step = () => (rail.children[0]?.getBoundingClientRect().width || rail.clientWidth) + gap();
  if (!ctrl) return;
  const dots = ctrl.querySelector('.s3-dots');
  const [prev, next] = ctrl.querySelectorAll('.s3-arr button');
  dots.innerHTML = '';
  for (let i = 0; i < rail.children.length; i++) dots.appendChild(document.createElement('i'));
  const sync = () => {
    const i = Math.round(rail.scrollLeft / step());
    [...dots.children].forEach((d, k) => d.classList.toggle('on', k === i));
    prev.disabled = rail.scrollLeft < 4;
    next.disabled = rail.scrollLeft + rail.clientWidth > rail.scrollWidth - 4;
    ctrl.hidden = rail.scrollWidth <= rail.clientWidth + 4;
  };
  prev.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
  next.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
  rail.addEventListener('scroll', sync, { passive: true });
  new ResizeObserver(sync).observe(rail);
  sync();
}

// mouse drag-to-scroll on desktop strips (touch swipes natively)
let dragMoved = false;
function initDrag() {
  if (!ready(document.documentElement, 's3Drag')) return;
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const el = e.target.closest('.s3-rail, .s3-oils__row, .s3-chips');
    if (!el || el.scrollWidth <= el.clientWidth) return;
    const x0 = e.clientX, s0 = el.scrollLeft, snap = getComputedStyle(el).scrollSnapType;
    dragMoved = false;
    const move = (ev) => { const dx = ev.clientX - x0; if (Math.abs(dx) > 5) { dragMoved = true; el.style.scrollSnapType = 'none'; } if (dragMoved) el.scrollLeft = s0 - dx; };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); el.style.scrollSnapType = snap; setTimeout(() => { dragMoved = false; }, 0); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  });
  document.addEventListener('click', (e) => { if (dragMoved && e.target.closest('.s3')) { e.preventDefault(); e.stopPropagation(); } }, true);
  document.addEventListener('dragstart', (e) => { if (e.target.closest('.s3-rail, .s3-oils__row')) e.preventDefault(); });
}

// ladder price, same maths as the live oil builder (sections/st-oil-builder.liquid)
const price = (n) => (n <= 0 ? 0 : n === 1 ? 14.99 : n === 2 ? 25.98 : n === 3 ? 35.97 : +(39.99 + (n - 4) * 9.99).toFixed(2));
const usd = (x) => '$' + x.toFixed(2);

function initOils(root) {
  if (!ready(root, 's3Ready')) return;
  const qty = new Map(); // variant id -> quantity (insertion order = pick order)
  const info = new Map(); // variant id -> { name, img }
  const pack = root.querySelector('[data-s3-pack]');
  const hint = pack.querySelector('.s3-pack__info span').textContent;
  const total = () => [...qty.values()].reduce((a, b) => a + b, 0);

  const slot = (card) => {
    const q = qty.get(card.dataset.id) || 0;
    const s = card.querySelector('.s3-qslot');
    if (!s || !s.querySelector('button')) return;
    s.innerHTML = q
      ? `<span class="s3-qty"><button type="button" data-d="-1" aria-label="Remove one ${card.dataset.name}">−</button><b>${q}</b><button type="button" data-d="1" aria-label="Add one ${card.dataset.name}">+</button></span>`
      : `<button type="button" class="s3-add" data-s3-add>+ Add</button>`;
  };
  const draw = () => {
    const n = total(), p = price(n), full = 14.99 * n;
    const thumbs = []; for (const [id, q] of qty) for (let i = 0; i < q && thumbs.length < 10; i++) thumbs.push(`<img src="${info.get(id).img}" alt="">`);
    const next = n === 0 ? hint : n < 4 ? `Add ${4 - n} more for ${usd(39.99 - price(n))} → 4 oils $39.99` : 'Best price unlocked · free shipping';
    pack.querySelector('.s3-pack__th').innerHTML = thumbs.join('') + '<i></i>'.repeat(Math.max(0, 4 - n));
    pack.querySelector('.s3-pack__info').innerHTML = (n ? `<b>${usd(p)}</b> for ${n} oil${n > 1 ? 's' : ''}${n > 1 ? `<s>${usd(full)}</s>` : ''}` : '<b>Build your pack</b>') + `<span>${next}</span>`;
    const btn = pack.querySelector('[data-s3-pack-add]');
    btn.disabled = n === 0; btn.textContent = n ? `Add ${n} to cart →` : 'Add to cart →';
    root.querySelectorAll('.s3-oc').forEach(slot);
  };

  root.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-s3-chip]');
    if (chip) {
      if (dragMoved) return;
      root.querySelectorAll('[data-s3-chip]').forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      root.querySelectorAll('[data-s3-panel]').forEach((p) => { p.hidden = p.dataset.s3Panel !== chip.dataset.s3Chip; });
      root.querySelectorAll('[data-s3-panel]:not([hidden]) .s3-oils__row').forEach(syncRow);
      chip.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
      return;
    }
    const nav = e.target.closest('.s3-oils__nav');
    if (nav) { const row = nav.parentElement.querySelector('.s3-oils__row'); const w = (row.children[0]?.getBoundingClientRect().width || 100) + 12; row.scrollBy({ left: w * 2 * Number(nav.dataset.dir), behavior: 'smooth' }); return; }
    const b = e.target.closest('[data-s3-add], .s3-qty button');
    if (b) {
      const card = b.closest('.s3-oc'); const id = card.dataset.id;
      info.set(id, { name: card.dataset.name, img: card.dataset.img });
      const d = b.matches('[data-s3-add]') ? 1 : Number(b.dataset.d);
      if (d > 0 && total() >= 10) return;
      const q = Math.max(0, (qty.get(id) || 0) + d);
      if (q) qty.set(id, q); else qty.delete(id);
      draw();
      return;
    }
    const add = e.target.closest('[data-s3-pack-add]');
    if (add && total()) addPack(add);
  });

  function syncRow(row) {
    const box = row.parentElement; const L = box.querySelector('.s3-oils__nav--l'), R = box.querySelector('.s3-oils__nav--r');
    L.disabled = row.scrollLeft < 4; R.disabled = row.scrollLeft + row.clientWidth > row.scrollWidth - 4;
  }
  root.querySelectorAll('.s3-oils__row').forEach((row) => { row.addEventListener('scroll', () => syncRow(row), { passive: true }); new ResizeObserver(() => syncRow(row)).observe(row); syncRow(row); });

  async function addPack(btn) {
    const items = [...qty].map(([id, quantity]) => ({ id: Number(id), quantity }));
    const sectionIds = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    btn.disabled = true; btn.setAttribute('aria-busy', 'true');
    pack.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })), promise: deferred.promise }));
    try {
      const r = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items, sections: sectionIds.join(',') }) }));
      const added = await r.json();
      if (!r.ok) throw new Error(added?.description || added?.message || 'Could not add to cart');
      const cart = await (await fetch(Theme.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 's3-oil-pack', sourceId: 's3-oil-pack', itemCount: total(), sections: added.sections, didError: false } });
      qty.clear(); draw();
    } catch (err) {
      deferred.reject(err);
      pack.dispatchEvent(new CartErrorEvent({ error: err instanceof Error ? err.message : 'Could not add to cart', code: 'INVALID' }));
      pack.querySelector('.s3-pack__info span').textContent = err instanceof Error ? err.message : 'Could not add to cart';
    } finally {
      btn.removeAttribute('aria-busy'); btn.disabled = total() === 0;
    }
  }
  draw();
}

function init() {
  initDrag();
  document.querySelectorAll('[data-s3-rail]').forEach(initRail);
  document.querySelectorAll('[data-s3-oils]').forEach(initOils);
}
init();
document.addEventListener('shopify:section:load', init);
